export class ApiError extends Error {
  constructor(status, body) {
    super(body?.error?.message ?? `HTTP ${status}`);
    this.status = status;
    this.code = body?.error?.code;
    this.requestId = body?.error?.requestId;
    this.body = body;
    this.retryAfterSeconds = Number(body?.error?.retryAfterSeconds) || 0;
  }
}

// Only read queries opt into this policy. Mutations and paid work are never retried here.
export function retryRead(failures, error) {
  return failures < 2 && (!error.status || error.status === 429 || error.status >= 500);
}
export function readRetryDelay(attempt, error) {
  return Math.max((error.retryAfterSeconds || 0) * 1000, Math.min(30_000 * 2 ** attempt, 120_000));
}
export function campaignPollInterval(query) {
  if (query.state.error) return false;
  return query.state.data?.status === 'running' ? 10_000 : false;
}

async function request(path, init = {}) {
  const res = await fetch(path, {
    ...init,
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
    credentials: 'same-origin',
  });
  const text = await res.text();
  let body = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) throw new ApiError(res.status, body);
  return body?.data ?? body;
}

export const api = {
  settings: () => request('/api/settings'),
  saveSettings: (input) => request('/api/settings', { method: 'PUT', body: JSON.stringify(input) }),
  internalSettings: () => request('/api/settings/internal'),
  saveInternalSettings: (input) =>
    request('/api/settings/internal', { method: 'PUT', body: JSON.stringify(input) }),
  listTenants: () => request('/api/tenants'),
  createTenant: (input) => request('/api/tenants', { method: 'POST', body: JSON.stringify(input) }),
  selectTenant: (id) => request(`/api/tenants/${id}/select`, { method: 'POST' }),
  tenantMembers: (id) => request(`/api/tenants/${id}/members`),
  setTenantMember: (id, input) =>
    request(`/api/tenants/${id}/members`, { method: 'PUT', body: JSON.stringify(input) }),
  me: () => request('/api/me'),
  logout: () => request('/api/auth/logout', { method: 'POST' }),
  listCampaigns: () => request('/api/campaigns'),
  createCampaign: (input) =>
    request('/api/campaigns', { method: 'POST', body: JSON.stringify(input) }),
  planCampaign: (id) => request(`/api/campaigns/${id}/plan`, { method: 'POST' }),
  startCampaign: (id) => request(`/api/campaigns/${id}/start`, { method: 'POST' }),
  updateCampaignBudget: (id, input) =>
    request(`/api/campaigns/${id}/budget`, { method: 'PUT', body: JSON.stringify(input) }),
  getCampaign: (id) => request(`/api/campaigns/${id}`),
  campaignLeads: (id) => request(`/api/campaigns/${id}/leads`),
  campaignCosts: (id) => request(`/api/campaigns/${id}/costs`),
  listLeads: (filters = {}) => request(`/api/leads?${new URLSearchParams(filters)}`),
  getLead: (id) => request(`/api/leads/${id}`),
  updateLead: (id, patch) =>
    request(`/api/leads/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  costSummary: () => request('/api/costs/summary'),
  costByFeature: () => request('/api/costs/by-feature'),
  costByProvider: () => request('/api/costs/by-provider'),
  budgets: () => request('/api/budgets'),
  createExport: (input) => request('/api/exports', { method: 'POST', body: JSON.stringify(input) }),
};

export function usdFromMicros(micros) {
  const n = Number(micros ?? 0) / 1_000_000;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'USD' });
}
